import { defaultSmDate } from 'utils/formatter';
import moment from 'moment-timezone';
import { QueryResult } from 'react-query';
import { callApi, Controller, useApi, UseApiOptions } from './useApi';
// eslint-disable-next-line import/no-cycle
import { addRechnungPosition, defaultRechnungPosition, RechnungPosition } from './rechnungPosition';
// eslint-disable-next-line import/no-cycle
import { getKunde, Kunde } from './kunde';
// eslint-disable-next-line import/no-cycle
import { Zusatzadresse } from './zusatzadresse';
// eslint-disable-next-line import/no-cycle
import { Auftrag } from './auftrag';
// eslint-disable-next-line import/no-cycle
import { AuftragPosition } from './auftragPosition';
import { LieferungPosition } from './lieferungPosition';
// eslint-disable-next-line import/no-cycle
import { Lieferung, updateLieferung } from './lieferung';

export interface Rechnung {
    kNr?: number;
    nr: string;
    kaNr: number;
    kaName: string;
    auNr: string;
    lNr: string;
    rechnungsAdrNr: number;
    status: number;
    datum: string;
    datBis: string;
    rahmen: boolean;
    betreuer: number;
    telBetreuer: number;
    txt: string;
    txtB1: number;
    txtB2: number;
    ara: number;
    liefBed: number;
    liefZeit: number;
    liefDat: string;
    liefKw: number;
    zahlBed: number;
    fracht: number;
    tipp: number;
    roadPr: number;
    abrMo: number;
    abrJahr: number;
    ekProzAuf: number;
    teilR: number;
    trProz: number;
    mwstCode: number;
    sNetto: number;
    sMwst: number;
    sBrutto: number;
    aktText: string;
    bezDat: string;
    anlageDat?: string;
    anlageUhr?: number;
    anlageUser?: string;
    updDat?: string;
    updUhr?: number;
    updUser?: string;

    kunde?: Kunde;
    rechnungsAdr?: Zusatzadresse;
    auftrag?: Auftrag;
    isExported?: boolean;
}

export function getDefaultRechnung(kNr?: number): Rechnung {
    const values: Rechnung = {
        kNr: undefined,
        nr: '',
        kaNr: 0,
        auNr: '',
        lNr: '',
        rechnungsAdrNr: 0,
        kaName: '',
        status: 0,
        datum: defaultSmDate,
        datBis: defaultSmDate,
        rahmen: false,
        betreuer: 0,
        telBetreuer: 0,
        txt: '',
        txtB1: 1,
        txtB2: 0,
        ara: 0,
        liefBed: 1,
        liefDat: defaultSmDate,
        liefZeit: 0,
        liefKw: 0,
        zahlBed: 1,
        fracht: 0,
        tipp: 0,
        roadPr: 0,
        abrMo: 0,
        abrJahr: 0,
        ekProzAuf: 0,
        teilR: 0,
        trProz: 0,
        mwstCode: 0,
        sNetto: 0,
        sMwst: 0,
        sBrutto: 0,
        aktText: '',
        bezDat: defaultSmDate,
    };

    const now = moment();

    return {
        ...values,
        kNr,
        datum: now.format('YYYY-MM-DD') + 'T00:00:00',
        // datBis: now.clone().add({ days: 7 }).format('YYYY-MM-DD') + 'T00:00:00',
    };
}

export interface RechnungFilter {
    nr?: string;
    nrSearch?: string;
    kNr?: number;
    auNr?: string;
    auNrSearch?: string;
    artikelGruppeNr?: number;
    artikelNr?: string;
    searchText?: string;
    betreuer?: number;
    betreuerExact?: number;
    telBetreuerExact?: number;
    datumVon?: string;
    datumBis?: string;
    kundePlzVon?: string;
    kundePlzBis?: string;
    kundeUmsatzVon?: number | null;
    kundeUmsatzBis?: number | null;
    kundeBranchen?: number[];
    kundeErweiterteSuchen?: number[];
    kundeInteresseFuerProdukte?: number[];
    kundeBetreuerExact?: number;
    kundeTelBetreuerExact?: number;
    produktgruppen?: number[];
    bezDatVon?: string;
    bezDatBis?: string;
    abrJahr?: number;
    abrMo?: number;
    lArt?: string;
    status?: number;
    statusList?: number[];
    withKunde?: boolean;
    withAuftrag?: boolean;
    withIsExported?: boolean;
    maxRows?: number;
    sortAsc?: boolean;
}

export function useRechnung(filter: RechnungFilter, apiOptions?: UseApiOptions<Rechnung>): QueryResult<Rechnung> {
    return useApi<Rechnung>(Controller.rechnung + '/one', { filter }, apiOptions);
}

export function useRechnungList(
    filter: RechnungFilter,
    apiOptions?: UseApiOptions<Rechnung[]>,
): QueryResult<Rechnung[]> {
    return useApi<Rechnung[]>(Controller.rechnung + '/list', { filter }, apiOptions);
}

interface AddRechnungResult {
    newNr: string;
}

export async function addRechnung(rechnung: Rechnung): Promise<AddRechnungResult> {
    return callApi<AddRechnungResult>(Controller.rechnung + '/add', { rechnung });
}

export async function updateRechnung(rechnung: Rechnung): Promise<void> {
    return callApi<void>(Controller.rechnung + '/update', { rechnung });
}

export async function deleteRechnung(rechnung: Rechnung): Promise<void> {
    return callApi<void>(Controller.rechnung + '/delete', { rechnung });
}

export async function sendRechnung(rechnung: Rechnung, kaNrListTo: number[], kaNrListCC: number[]): Promise<void> {
    return callApi<void>(Controller.rechnung + '/send', { nr: rechnung.nr, kaNrListTo, kaNrListCC });
}

export async function addRechnungFromAuftrag(
    kunde: Kunde,
    auftrag: Auftrag,
    auftragPositionen: AuftragPosition[],
    trNum: number,
    trProz: number,
): Promise<AddRechnungResult> {
    const defaultRechnung = getDefaultRechnung();

    const kNr = auftrag.rechnungsKNr ? auftrag.rechnungsKNr : auftrag.kNr || 0;

    const newRechnung: Rechnung = {
        ...defaultRechnung,
        kNr,
        kaNr: auftrag.kaNr,
        auNr: auftrag.nr,
        rechnungsAdrNr: auftrag.rechnungsKNr ? defaultRechnung.rechnungsAdrNr : auftrag.rechnungsAdrNr,
        betreuer: auftrag.betreuer,
        telBetreuer: kunde.telBetreuer,
        txt: auftrag.txt,
        liefBed: auftrag.liefBed,
        liefDat: auftrag.liefDat,
        liefZeit: auftrag.liefZeit,
        liefKw: auftrag.liefKw,
        zahlBed: auftrag.zahlBed,
        ara: auftrag.ara,
        aktText: auftrag.aktText,
        mwstCode: auftrag.mwstCode,
        fracht: auftrag.fracht,
        tipp: auftrag.tipp,
        roadPr: auftrag.roadPr,
        teilR: trNum,
        trProz,
    };

    const result = await addRechnung(newRechnung);

    if (auftragPositionen) {
        let pos = 1;
        // eslint-disable-next-line no-restricted-syntax
        for (const auftragPosition of auftragPositionen) {
            if (auftragPosition.transfer) {
                // eslint-disable-next-line no-await-in-loop
                await addRechnungPosition({
                    ...defaultRechnungPosition,
                    rNr: result.newNr,
                    pos,
                    artikelGruppeNr: auftragPosition.artikelGruppeNr,
                    artikelNr: auftragPosition.artikelNr,
                    posText: auftragPosition.posText,
                    posEinh: auftragPosition.posEinh,
                    posMenge: auftragPosition.transferMenge || 0,
                    posAMenge: auftragPosition.posMenge,
                    posEkPreis: auftragPosition.posEkPreis,
                    posEPreis: auftragPosition.posEPreis,
                    posNetto: auftragPosition.posNetto,
                    posMwst: auftragPosition.posMwst,
                    posBrutto: auftragPosition.posBrutto,
                    posBem: auftragPosition.posBem,
                });
                pos += 1;
            }
        }
    }

    return result;
}

export async function addRechnungFromLieferung(
    kunde: Kunde,
    lieferung: Lieferung,
    lieferungPositionen?: LieferungPosition[],
): Promise<AddRechnungResult> {
    const defaultRechnung = getDefaultRechnung();

    const newRechnung: Rechnung = {
        ...defaultRechnung,
        kNr: lieferung.kNr,
        kaNr: lieferung.kaNr,
        auNr: lieferung.auNr,
        lNr: lieferung.nr,
        betreuer: lieferung.betreuer,
        telBetreuer: kunde.telBetreuer,
        txt: lieferung.txt,
        aktText: lieferung.aktText,
        liefBed: lieferung.liefBed,
        liefDat: lieferung.liefDat,
        liefZeit: lieferung.liefZeit,
        liefKw: lieferung.liefKw,
        zahlBed: lieferung.zahlBed,
        ara: lieferung.ara,
        mwstCode: lieferung.mwstCode,
    };

    const result = await addRechnung(newRechnung);

    if (lieferungPositionen) {
        // eslint-disable-next-line no-restricted-syntax
        for (const lieferungPosition of lieferungPositionen) {
            // eslint-disable-next-line no-await-in-loop
            await addRechnungPosition({
                ...defaultRechnungPosition,
                rNr: result.newNr,
                artikelGruppeNr: lieferungPosition.artikelGruppeNr,
                artikelNr: lieferungPosition.artikelNr,
                pos: lieferungPosition.pos,
                posText: lieferungPosition.posText,
                posEinh: lieferungPosition.posEinh,
                posMenge: lieferungPosition.posMenge,
                posAMenge: lieferungPosition.posAMenge,
                posEkPreis: lieferungPosition.posEkPreis,
                posEPreis: lieferungPosition.posEPreis,
                posNetto: lieferungPosition.posNetto,
                posMwst: lieferungPosition.posMwst,
                posBrutto: lieferungPosition.posBrutto,
                posBem: lieferungPosition.posBem,
            });
        }
    }

    await updateLieferung({
        ...lieferung,
        status: 4, // abgeschlossen
    });

    return result;
}

export async function copyRechnung(
    kNr: number,
    rechnung: Rechnung,
    rechnungPositionen?: RechnungPosition[],
): Promise<AddRechnungResult> {
    const newKundeList = await getKunde({ nr: kNr });

    if (newKundeList.length !== 1) {
        throw new Error('Kunde nicht gefunden');
    }

    const newKunde = newKundeList[0];

    const defaultRechnung = getDefaultRechnung();

    const newRechnung = {
        ...rechnung,
        kNr,
        status: 0,
        datum: defaultRechnung.datum,
        datBis: defaultRechnung.datBis,
        betreuer: newKunde.betreuer || defaultRechnung.betreuer,
        telBetreuer: newKunde.telBetreuer || defaultRechnung.telBetreuer,
        zahlBed: newKunde.zahlZiel || defaultRechnung.zahlBed,
        isExported: false,
    };

    const result = await addRechnung(newRechnung);

    if (rechnungPositionen) {
        // eslint-disable-next-line no-restricted-syntax
        for (const rechnungPosition of rechnungPositionen) {
            // eslint-disable-next-line no-await-in-loop
            await addRechnungPosition({ ...rechnungPosition, rNr: result.newNr });
        }
    }

    return result;
}

interface RecalculateRechnungResult {
    rechnung: Rechnung;
}

export async function recalculateRechnung(rechnung: Rechnung): Promise<RecalculateRechnungResult> {
    return callApi<RecalculateRechnungResult>(Controller.rechnung + '/recalculate', { rechnung });
}

interface MarkRechnungForExportResult {
    rechnung: Rechnung;
}

export async function markRechnungForExport(rechnung: Rechnung): Promise<MarkRechnungForExportResult> {
    return callApi<MarkRechnungForExportResult>(Controller.rechnung + '/markforexport', { rechnung });
}
